import { IconNameType, Page, Tile } from 'components';
import { useBreakpoint } from 'hooks';
import React from 'react';
import { useHistory } from 'react-router';
import { RouteComponentProps } from 'react-router-dom';
import styled from 'styled-components';
import { buildCssProp } from 'utils';
import AutomatedTransfersPage from './AutomatedTransfersPage';
import DirectLoginPage from './DirectLoginPage';
import ForceVerifyPage from './ForceVerifyPage';
import ProcessDuplicatesPage from './ProcessDuplicatesPage';
import ResetVerifyPage from './ResetVerifyPage';
import TestSsoFlowPage from './TestSsoFlowPage';
import ListingCronJobsPage from './ListingCronJobsPage';
import SupraCronjobsPage from './SupraCronjobsPage';
import TriggerRemoveRetsOpenHouse from './TriggerRetsUpdates/TriggerRemoveRetsOpenHouse';
import TriggerRetsListingUpdatePage from './TriggerRetsUpdates/TriggerRetsListingUpdatePage';
import TriggerRetsOpenHouseUpdatePage from './TriggerRetsUpdates/TriggerRetsOpenHouseUpdatePage';
import RosterDataViewerPage from './RosterDataViewerPage';
import InvalidateMobileTokensPage from './InvalidateMobileTokens';
import TriggerOfficeUpdatesForUsers from './TriggerRetsUpdates/TriggerOfficeUpdatesForUsers';

type State =
  | 'main'
  | 'automatedTransfers'
  | 'forceVerify'
  | 'resetVerify'
  | 'resetIsClaimed'
  | 'addTeamUsers'
  | 'addUpdateTeams'
  | 'testSsoFlow'
  | 'processDuplicates'
  | 'testDirectLogin'
  | 'listingCronJobs'
  | 'supraSystemCronjobs'
  | 'triggerRemoveRetsOpenHouse'
  | 'triggerRetsListingUpdate'
  | 'triggerRetsOpenHouseUpdate'
  | 'rosterDataViewer'
  | 'invalidatMobileTokens'
  | 'triggerOfficeUpdatesForUsers';

type ButtonItemType = {
  state: State;
  lines: [string, string];
  icon?: IconNameType;
};

const PAGES: ButtonItemType[] = [
  {
    state: 'automatedTransfers',
    lines: ['Automated', 'Transfers'],
  },
  {
    state: 'forceVerify',
    lines: ['Force', 'Verify'],
  },
  {
    state: 'resetVerify',
    lines: ['Reset', 'Verify'],
  },
  {
    state: 'resetIsClaimed',
    lines: ['Reset', 'Claimed Status'],
  },
  {
    state: 'addTeamUsers',
    lines: ['Add', 'Team Users'],
  },
  {
    state: 'addUpdateTeams',
    lines: ['Add/Update', 'Teams'],
  },
  {
    state: 'testSsoFlow',
    lines: ['Test OIDC', 'SSO Flow'],
  },
  { state: 'processDuplicates', lines: ['Process', 'Duplicate Listings'] },
  { state: 'testDirectLogin', lines: ['Test', 'Direct Login'] },
  { state: 'listingCronJobs', lines: ['Listing', 'Cron Jobs'] },
  { state: 'supraSystemCronjobs', lines: ['Supra', 'Cron Jobs'] },
  {
    state: 'triggerRemoveRetsOpenHouse',
    lines: ['Trigger RETS', 'Remove Open House'],
    icon: 'antdDeleteFilled',
  },
  {
    state: 'triggerRetsListingUpdate',
    lines: ['Trigger RETS', 'Listing Update'],
    icon: 'home',
  },
  {
    state: 'triggerRetsOpenHouseUpdate',
    lines: ['Trigger RETS', 'Open House Update'],
    icon: 'home',
  },
  {
    state: 'rosterDataViewer',
    lines: ['Roster Data', 'Viewer'],
    icon: 'antdUserOutlined',
  },
  {
    state: 'invalidatMobileTokens',
    lines: ['Invalidate', 'Mobile Tokens'],
    icon: 'antdLoadingOutlined',
  },
  {
    state: 'triggerOfficeUpdatesForUsers',
    lines: ['Trigger Office Updates', 'For Users'],
    icon: 'antdExclamationCircleFilled',
  },
];

const QAPages: React.FC<RouteComponentProps<{ state?: State }>> = ({
  match,
}) => {
  const { md } = useBreakpoint();
  const history = useHistory();
  const [pageState, setPageState] = React.useState<State>(
    match.params.state || 'main',
  );

  React.useEffect(() => {
    setPageState(match.params.state || 'main');
  }, [match]);

  React.useEffect(() => {
    switch (pageState) {
      case 'main':
      case 'automatedTransfers':
      case 'processDuplicates':
      case 'forceVerify':
      case 'resetVerify':
      case 'testSsoFlow':
      case 'testDirectLogin':
      case 'listingCronJobs':
      case 'supraSystemCronjobs':
      case 'triggerRemoveRetsOpenHouse':
      case 'triggerRetsListingUpdate':
      case 'triggerRetsOpenHouseUpdate':
      case 'rosterDataViewer':
      case 'invalidatMobileTokens':
      case 'triggerOfficeUpdatesForUsers': {
        history.push(`/brokerbay_admin/qa/${pageState}`);
        break;
      }

      // Existing pages
      // Can be migrated to inline page later
      case 'resetIsClaimed': {
        history.push('/brokerbay_admin/onboarding_tools');
        break;
      }

      case 'addTeamUsers': {
        history.push('/brokerbay_admin/add_users_to_teams');
        break;
      }

      case 'addUpdateTeams': {
        history.push('/brokerbay_admin/add_or_update_teams');
        break;
      }

      default: {
        setPageState('main');
        break;
      }
    }
  }, [history, pageState]);

  switch (pageState) {
    case 'resetIsClaimed':
    case 'addTeamUsers':
    case 'addUpdateTeams':
    case 'main': {
      return (
        <Page type="hollow" title="QA Pages">
          <StyledGrid $isMobile={!md}>
            {PAGES.map(({ state, lines, icon }) => (
              <Tile
                key={state}
                icon={icon || 'tool'}
                lines={lines}
                dataCy={`qa-page-${state}`}
                onClick={() => setPageState(state)}
              />
            ))}
          </StyledGrid>
        </Page>
      );
    }

    case 'processDuplicates': {
      return <ProcessDuplicatesPage />;
    }

    case 'forceVerify': {
      return <ForceVerifyPage />;
    }

    case 'resetVerify': {
      return <ResetVerifyPage />;
    }

    case 'automatedTransfers': {
      return <AutomatedTransfersPage />;
    }

    case 'testSsoFlow': {
      return <TestSsoFlowPage />;
    }

    case 'testDirectLogin': {
      return <DirectLoginPage />;
    }

    case 'listingCronJobs': {
      return <ListingCronJobsPage />;
    }

    case 'supraSystemCronjobs': {
      return <SupraCronjobsPage />;
    }

    case 'triggerRemoveRetsOpenHouse': {
      return <TriggerRemoveRetsOpenHouse />;
    }

    case 'triggerRetsListingUpdate': {
      return <TriggerRetsListingUpdatePage />;
    }

    case 'triggerRetsOpenHouseUpdate': {
      return <TriggerRetsOpenHouseUpdatePage />;
    }

    case 'rosterDataViewer': {
      return <RosterDataViewerPage />;
    }

    case 'invalidatMobileTokens': {
      return <InvalidateMobileTokensPage />;
    }
    case 'triggerOfficeUpdatesForUsers': {
      return <TriggerOfficeUpdatesForUsers />;
    }

    default: {
      setPageState('main');
      return null;
    }
  }
};

export default QAPages;

const StyledGrid = styled.div<{ $isMobile: boolean }>`
  display: grid;
  gap: 35px;
  grid-auto-rows: 112px;
  grid-template-columns: repeat(auto-fit, 235px);
  ${({ $isMobile }) =>
    buildCssProp('grid-template-columns', $isMobile && '1fr')};
`;
